import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Globe, UserCog, Upload, Trash2, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TenantManageDialog = ({ tenant, open, onOpenChange, onSaved }) => {
  const fileRef = useRef(null);
  const [mode, setMode] = useState('client');
  const [clientDpo, setClientDpo] = useState({ dpo_name: '', dpo_email: '', dpo_password: '' });
  const [banner, setBanner] = useState(null);
  const [retention, setRetention] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setMode(tenant.dpo_mode || 'client');
    setBanner(tenant.login_banner_url || null);
    setRetention(tenant.audit_retention_days ? String(tenant.audit_retention_days) : '');
    setClientDpo({ dpo_name: '', dpo_email: '', dpo_password: '' });
  }, [tenant]);

  if (!tenant) return null;

  const saveMode = async () => {
    setBusy(true);
    try {
      const payload = mode === 'client' ? { mode, ...clientDpo } : { mode };
      await axios.post(`${BACKEND_URL}/api/super-admin/tenants/${tenant.tenant_id}/dpo-mode`, payload, { withCredentials: true });
      toast.success(mode === 'cogniseg' ? 'Organização ligada ao DPO as a Service' : 'DPO do cliente configurado');
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar o modo de DPO');
    } finally {
      setBusy(false);
    }
  };

  const saveRetention = async () => {
    const raw = retention.trim();
    if (!raw) {
      try {
        await axios.patch(`${BACKEND_URL}/api/tenants/${tenant.tenant_id}`, { audit_retention_days: null }, { withCredentials: true });
        toast.success('Retenção do tenant limpa (usa o valor global)');
        onSaved?.();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao guardar retenção');
      }
      return;
    }
    const value = parseInt(raw, 10);
    if (!Number.isFinite(value) || value < 30) {
      toast.error('Mínimo: 30 dias');
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/tenants/${tenant.tenant_id}`, { audit_retention_days: value }, { withCredentials: true });
      toast.success(`Retenção definida em ${value} dias`);
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar retenção');
    } finally {
      setBusy(false);
    }
  };

  const uploadBanner = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(
        `${BACKEND_URL}/api/branding/tenants/${tenant.tenant_id}/banner`,
        form,
        { withCredentials: true }
      );
      setBanner(data.login_banner_url);
      toast.success('Banner atualizado');
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar o banner');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeBanner = async () => {
    setBusy(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/branding/tenants/${tenant.tenant_id}/banner`, { withCredentials: true });
      setBanner(null);
      toast.success('Banner removido');
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="tenant-manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5" /> {tenant.name}
          </DialogTitle>
          <DialogDescription>Modo de DPO e personalização visual da organização</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Label>Modo de DPO</Label>
              <Badge variant={tenant.dpo_mode === 'cogniseg' ? 'default' : 'outline'} data-testid="current-dpo-mode">
                {tenant.dpo_mode === 'cogniseg' ? 'DPO as a Service' : 'DPO do cliente'}
              </Badge>
            </div>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="dpo-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cogniseg">DPO as a Service (serviço global)</SelectItem>
                <SelectItem value="client">DPO do cliente (utilizador local)</SelectItem>
              </SelectContent>
            </Select>

            {mode === 'cogniseg' ? (
              <p className="text-sm text-slate-500 flex items-start gap-2">
                <Globe className="w-4 h-4 mt-0.5 text-[#10B981]" />
                O utilizador global passa a ver esta organização no seletor e recebe as notificações operacionais.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-3 pt-1">
                <Input
                  placeholder="Nome do DPO"
                  value={clientDpo.dpo_name}
                  onChange={(e) => setClientDpo({ ...clientDpo, dpo_name: e.target.value })}
                  data-testid="client-dpo-name"
                />
                <Input
                  placeholder="Email do DPO"
                  value={clientDpo.dpo_email}
                  onChange={(e) => setClientDpo({ ...clientDpo, dpo_email: e.target.value })}
                  data-testid="client-dpo-email"
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={clientDpo.dpo_password}
                  onChange={(e) => setClientDpo({ ...clientDpo, dpo_password: e.target.value })}
                  data-testid="client-dpo-password"
                />
              </div>
            )}
            <Button onClick={saveMode} disabled={busy} data-testid="save-dpo-mode-button">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Aplicar modo de DPO
            </Button>
          </div>

          <div className="space-y-3 pt-6 border-t border-slate-200 dark:border-slate-700">
            <Label className="flex items-center gap-2"><Clock className="w-4 h-4" /> Retenção de audit_logs (dias)</Label>
            <div className="flex flex-wrap items-end gap-3">
              <Input
                type="number"
                min="30"
                value={retention}
                onChange={(e) => setRetention(e.target.value)}
                placeholder="usa valor global"
                className="w-52"
                data-testid="tenant-retention-input"
              />
              <Button variant="outline" onClick={saveRetention} disabled={busy} data-testid="save-tenant-retention-button">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar retenção
              </Button>
            </div>
            <p className="text-xs text-slate-500 max-w-xl">
              Deixe vazio para herdar a retenção global (definida em Consola > Base de Dados).
              Setores regulados (banca, saúde) podem exigir períodos mais longos.
              A purga automática respeita este valor e cria um backup em Object Storage antes de apagar.
            </p>
          </div>

          <div className="space-y-3 pt-6 border-t border-slate-200 dark:border-slate-700">
            <Label>Banner da página de login (1820x400)</Label>
            {banner ? (
              <img
                src={`${BACKEND_URL}${banner}?t=${Date.now()}`}
                alt="Banner"
                data-testid="tenant-banner-admin-preview"
                className="w-full rounded-lg border border-slate-200 object-cover"
                style={{ aspectRatio: '1820 / 400' }}
              />
            ) : (
              <div
                className="w-full rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-sm"
                style={{ aspectRatio: '1820 / 400' }}
              >
                Sem banner definido
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={uploadBanner}
              className="hidden"
              data-testid="admin-banner-file-input"
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="admin-upload-banner-button">
                <Upload className="w-4 h-4 mr-2" /> {banner ? 'Substituir' : 'Carregar'}
              </Button>
              {banner && (
                <Button variant="outline" onClick={removeBanner} disabled={busy} data-testid="admin-remove-banner-button">
                  <Trash2 className="w-4 h-4 mr-2" /> Remover
                </Button>
              )}
            </div>
            {tenant.slug && (
              <p className="text-xs text-slate-500">
                URL: {window.location.origin}/login-local?org={tenant.slug}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-manage-dialog">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TenantManageDialog;
